import { useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { Button } from '@actual-app/components/button';
import { Input } from '@actual-app/components/input';
import { Select } from '@actual-app/components/select';
import { Text } from '@actual-app/components/text';
import { theme } from '@actual-app/components/theme';
import { View } from '@actual-app/components/view';
import { FRANKFURTER_SUPPORTED_CURRENCIES } from '@actual-app/core/server/exchange-rates/frankfurter';
import { currencies } from '@actual-app/core/shared/currencies';
import * as monthUtils from '@actual-app/core/shared/months';
import { getNumberFormat } from '@actual-app/core/shared/util';
import { useQuery } from '@tanstack/react-query';
import { v4 as uuidv4 } from 'uuid';

import {
  exchangeRateQueries,
  useCreateExchangeRateMutation,
  useDeleteExchangeRateMutation,
  useFetchExchangeRatesMutation,
} from '#exchange-rates';
import { addNotification } from '#notifications/notificationsSlice';
import { useDispatch } from '#redux';

import { useCurrencyOptions } from './Currency';
import { Setting } from './UI';

// Real currency codes only -- unlike the account-currency picker, there's
// no meaningful "budget default" entry for an exchange rate: a rate is
// always between two specific currencies.
function useRealCurrencyOptions(): [string, string][] {
  const options = useCurrencyOptions();
  return useMemo(() => options.filter(([code]) => code !== ''), [options]);
}

function useFrankfurterCurrencyOptions(): [string, string][] {
  const options = useRealCurrencyOptions();
  return useMemo(
    () =>
      options.filter(([code]) => FRANKFURTER_SUPPORTED_CURRENCIES.has(code)),
    [options],
  );
}

// Parses a rate using the budget's own configured thousands/decimal
// separators (getNumberFormat()) -- the same convention every other
// numeric field in the app already uses -- rather than guessing intent
// from the string's shape.
//
// A prior version treated any single comma-or-dot as *the* decimal
// separator, which broke on legitimately thousands-grouped rates (e.g.
// USD -> IDR "16,000" under a comma-thousands format got parsed as 16,
// not 16000 -- common for currencies like IDR/KRW/IRR whose rates are
// large). Before that, an even earlier version used currencyToAmount
// directly, whose own thousands-vs-decimal heuristic broke the opposite
// way on small 3-decimal rates (e.g. "0,923" parsed as 923). Explicitly
// stripping the configured thousands separator first, then normalizing
// the configured decimal separator to '.', resolves both: it's the same
// unambiguous rule the rest of the app already applies to number entry,
// not a heuristic guess.
function parseRateInput(value: string): number | null {
  const {
    value: format,
    thousandsSeparator,
    decimalSeparator,
  } = getNumberFormat();

  // Apostrophe-dot: getNumberFormat() reports the typographic apostrophe
  // (U+2019) as its thousands separator, but keyboards produce the plain
  // ASCII apostrophe (U+0027) -- accept both, same as the existing
  // reapplyThousandSeparators() does for this same format.
  const stripThousands =
    format === 'apostrophe-dot'
      ? (s: string) => s.replaceAll(/[’']/g, '')
      : (s: string) => s.split(thousandsSeparator).join('');

  const normalized = stripThousands(value.trim()).replace(
    decimalSeparator,
    '.',
  );

  if (!/^\d+(\.\d+)?$/.test(normalized)) return null;
  const rate = Number(normalized);
  return Number.isFinite(rate) ? rate : null;
}

export function ExchangeRateSettings() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const currencyOptions = useRealCurrencyOptions();
  const frankfurterCurrencyOptions = useFrankfurterCurrencyOptions();

  const { data: rates = [] } = useQuery(exchangeRateQueries.list());
  const createRate = useCreateExchangeRateMutation();
  const deleteRate = useDeleteExchangeRateMutation();
  const fetchRates = useFetchExchangeRatesMutation();

  const firstCurrency = currencies.find(c => c.code !== '')?.code ?? '';
  const firstFrankfurterCurrency = frankfurterCurrencyOptions[0]?.[0] ?? '';

  const [manualFrom, setManualFrom] = useState(firstCurrency);
  const [manualTo, setManualTo] = useState(firstCurrency);
  const [manualDate, setManualDate] = useState(monthUtils.currentDay());
  const [manualRate, setManualRate] = useState('');

  const [fetchFrom, setFetchFrom] = useState(firstFrankfurterCurrency);
  const [fetchTo, setFetchTo] = useState(firstFrankfurterCurrency);
  const [fetchStart, setFetchStart] = useState(monthUtils.currentDay());
  const [fetchEnd, setFetchEnd] = useState(monthUtils.currentDay());

  const onAddManualRate = () => {
    const rate = parseRateInput(manualRate);
    if (
      !manualFrom ||
      !manualTo ||
      manualFrom === manualTo ||
      !manualDate ||
      rate == null ||
      rate <= 0
    ) {
      return;
    }

    createRate.mutate({
      from_currency: manualFrom,
      to_currency: manualTo,
      date: manualDate,
      rate,
    });
    setManualRate('');
  };

  const onFetchRates = () => {
    if (!fetchFrom || !fetchTo || fetchFrom === fetchTo) return;

    fetchRates.mutate(
      {
        from_currency: fetchFrom,
        to_currency: fetchTo,
        start_date: fetchStart,
        end_date: fetchEnd,
      },
      {
        onSuccess: ({ stored }) => {
          dispatch(
            addNotification({
              notification: {
                id: uuidv4(),
                type: 'message',
                message: t('Fetched and stored {{count}} exchange rates.', {
                  count: stored,
                }),
              },
            }),
          );
        },
      },
    );
  };

  return (
    <Setting
      primaryAction={
        <View style={{ display: 'flex', flexDirection: 'column', gap: '1em' }}>
          <View>
            <Text style={{ fontWeight: 600 }}>
              <Trans>Add a rate manually</Trans>
            </Text>
            <View
              style={{
                display: 'flex',
                flexDirection: 'row',
                gap: '0.5em',
                marginTop: '0.5em',
                alignItems: 'center',
              }}
            >
              <Select
                value={manualFrom}
                onChange={setManualFrom}
                options={currencyOptions}
                style={{ width: 160 }}
              />
              <Text>→</Text>
              <Select
                value={manualTo}
                onChange={setManualTo}
                options={currencyOptions}
                style={{ width: 160 }}
              />
              <Input
                type="date"
                value={manualDate}
                onChangeValue={setManualDate}
                style={{ width: 140 }}
              />
              <Input
                inputMode="decimal"
                placeholder={t('Rate')}
                value={manualRate}
                onChangeValue={setManualRate}
                style={{ width: 100 }}
              />
              <Button onPress={onAddManualRate}>
                <Trans>Add</Trans>
              </Button>
            </View>
          </View>

          <View>
            <Text style={{ fontWeight: 600 }}>
              <Trans>Fetch rates automatically</Trans>
            </Text>
            <Text style={{ fontSize: '0.85em', color: theme.pageTextLight }}>
              <Trans>
                Pulls daily rates from Frankfurter (frankfurter.dev), an ECB-
                based rate source. No account or API key required.
              </Trans>
            </Text>
            <View
              style={{
                display: 'flex',
                flexDirection: 'row',
                gap: '0.5em',
                marginTop: '0.5em',
                alignItems: 'center',
              }}
            >
              <Select
                value={fetchFrom}
                onChange={setFetchFrom}
                options={frankfurterCurrencyOptions}
                style={{ width: 160 }}
              />
              <Text>→</Text>
              <Select
                value={fetchTo}
                onChange={setFetchTo}
                options={frankfurterCurrencyOptions}
                style={{ width: 160 }}
              />
              <Input
                type="date"
                value={fetchStart}
                onChangeValue={setFetchStart}
                style={{ width: 140 }}
              />
              <Text>{t('to')}</Text>
              <Input
                type="date"
                value={fetchEnd}
                onChangeValue={setFetchEnd}
                style={{ width: 140 }}
              />
              <Button onPress={onFetchRates} isDisabled={fetchRates.isPending}>
                <Trans>Fetch</Trans>
              </Button>
            </View>
          </View>

          {rates.length > 0 && (
            <View>
              <Text style={{ fontWeight: 600 }}>
                <Trans>Stored rates</Trans>
              </Text>
              <View style={{ marginTop: '0.5em' }}>
                {rates.map(rate => (
                  <View
                    key={rate.id}
                    style={{
                      display: 'flex',
                      flexDirection: 'row',
                      alignItems: 'center',
                      gap: '0.75em',
                      padding: '0.25em 0',
                      borderBottom: `1px solid ${theme.tableBorder}`,
                    }}
                  >
                    <Text style={{ width: 200 }}>
                      {rate.from_currency} → {rate.to_currency}
                    </Text>
                    <Text style={{ width: 120 }}>{rate.date}</Text>
                    <Text style={{ flex: 1 }}>{rate.rate}</Text>
                    <Button onPress={() => deleteRate.mutate({ id: rate.id })}>
                      <Trans>Delete</Trans>
                    </Button>
                  </View>
                ))}
              </View>
            </View>
          )}
        </View>
      }
    >
      <Text>
        <Trans>
          <strong>Exchange rates</strong> let accounts with a different currency
          than the rest of your budget be converted for aggregate views. Add
          rates manually, or fetch them automatically for a date range.
        </Trans>
      </Text>
    </Setting>
  );
}
